import React, { useReducer, useEffect, type ReactNode } from 'react';
import axios, { type AxiosError } from 'axios';
import { QueryClient, QueryClientProvider } from 'react-query';

import { API_URL } from 'consts';
import { getCookie, setCookie, removeCookie } from 'utils';
import type { AuthErrorResponse, AuthUser, LoginResponse } from 'types/api';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 60,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

interface AuthStateShape {
  access_token: string | false | null;
  user: AuthUser | null;
  error: AuthErrorResponse | null;
  loading: boolean;
}

interface Credentials {
  username: string;
  password: string;
}

interface AuthContextValue extends AuthStateShape {
  authenticate: (credentials: Credentials) => Promise<void>;
  unauthenticate: () => void;
  getCurrentUser: () => Promise<void>;
}

type AuthAction =
  | { type: 'AUTHENTICATE_START' }
  | { type: 'AUTHENTICATE_SUCCESS'; token: string; user: AuthUser }
  | { type: 'AUTHENTICATE_FAIL'; error: AuthErrorResponse | null }
  | { type: 'SET_ACCESS_TOKEN'; token: string }
  | { type: 'NO_ACCESS_TOKEN' }
  | { type: 'SET_CURRENT_USER'; user: AuthUser }
  | { type: 'UNAUTHENTICATE' };

const initialState: AuthStateShape = {
  access_token: null,
  user: null,
  error: null,
  loading: false,
};

const noopAsync = async () => {};

export const AuthState = React.createContext<AuthContextValue>({
  ...initialState,
  authenticate: noopAsync,
  unauthenticate: () => {},
  getCurrentUser: noopAsync,
});

const reducer = (state: AuthStateShape, action: AuthAction): AuthStateShape => {
  switch (action.type) {
    case 'AUTHENTICATE_START':
      return { ...state, loading: true, error: null };
    case 'AUTHENTICATE_SUCCESS':
      return { ...state, access_token: action.token, user: action.user, loading: false };
    case 'AUTHENTICATE_FAIL':
      return { ...state, loading: false, error: action.error };
    case 'SET_ACCESS_TOKEN':
      return { ...state, access_token: action.token };
    case 'NO_ACCESS_TOKEN':
      return { ...state, access_token: false };
    case 'SET_CURRENT_USER':
      return { ...state, user: action.user };
    case 'UNAUTHENTICATE':
      return { ...initialState, access_token: false };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const token = getCookie('dtd_at');

    if (typeof token === 'string' && !state.access_token) {
      dispatch({ type: 'SET_ACCESS_TOKEN', token });
    } else {
      dispatch({ type: 'NO_ACCESS_TOKEN' });
    }
    // eslint-disable-next-line
  }, []);

  const authenticate = async ({ username, password }: Credentials) => {
    dispatch({ type: 'AUTHENTICATE_START' });

    try {
      const res = await axios.post<LoginResponse>(`${API_URL}/login`, {
        username,
        password,
      });

      dispatch({ type: 'AUTHENTICATE_SUCCESS', token: res.data.token, user: res.data.user });
      setCookie('dtd_at', res.data.token);
    } catch (error) {
      const err = error as AxiosError<AuthErrorResponse>;
      dispatch({ type: 'AUTHENTICATE_FAIL', error: err.response?.data || null });
    }
  };

  const unauthenticate = () => {
    removeCookie('dtd_at');
    dispatch({ type: 'UNAUTHENTICATE' });
    (window.location.reload as unknown as (forcedReload?: boolean) => void)(true);
  };

  const getCurrentUser = async () => {
    try {
      const res = await axios.get<{ user: AuthUser }>(`${API_URL}/user/current`, {
        headers: { authorization: `Bearer: ${state.access_token}` },
      });

      dispatch({ type: 'SET_CURRENT_USER', user: res.data.user });
    } catch (_error) {
      unauthenticate();
    }
  };

  const value: AuthContextValue = {
    ...state,
    authenticate,
    unauthenticate,
    getCurrentUser,
  };

  return (
    <AuthState.Provider value={value}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </AuthState.Provider>
  );
};
